import React, { useState, useEffect } from 'react';
import { Package, MapPin, CheckCircle, XCircle, Clock, Box, Ban, AlarmClock, Zap } from 'lucide-react';
import { DeliveryListModal } from './DeliveryListModal';

const API_URL = process.env.REACT_APP_API_URL;

export function StatisticsCards({
  deliveries,
  deliveriesProvince,
  orders,
  selectedDate,
  expressDeliveries = [],
}) {
  const [modal, setModal] = useState(null);

  const [postponedItems, setPostponedItems]     = useState([]);
  const [postponedLoading, setPostponedLoading] = useState(false);

  useEffect(() => {
    if (!selectedDate) return;
    async function fetchPostponed() {
      setPostponedLoading(true);
      try {
        const res  = await fetch(`${API_URL}/api/delivery-points/postponed?date=${selectedDate}`);
        const data = await res.json();
        setPostponedItems(Array.isArray(data) ? data : (data.deliveries ?? []));
      } catch (err) {
        console.error('Error fetching postponed deliveries:', err);
        setPostponedItems([]);
      } finally {
        setPostponedLoading(false);
      }
    }
    fetchPostponed();
  }, [selectedDate]);

  // Lima stats
  const limaEntregado = deliveries.filter(d => d.status === 'entregado');
  const limaRechazado = deliveries.filter(d => d.status === 'rechazado');
  const limaPending   = deliveries.filter(d => d.status === 'pending');
  const limaInTransit = deliveries.filter(d => d.status === 'in_transit');
  const limaCancelado = deliveries.filter(d => d.status === 'cancelado');
  const limaDropship  = deliveries.filter(d => d.provider_id != null);

  // Provincia stats
  const provEntregado = deliveriesProvince.filter(d => d.status === 'entregado');
  const provRechazado = deliveriesProvince.filter(d => d.status === 'rechazado');
  const provPending   = deliveriesProvince.filter(d => d.status === 'pending');
  const provInTransit = deliveriesProvince.filter(d => d.status === 'in_transit');
  const provCancelado = deliveriesProvince.filter(d => d.status === 'cancelado');
  const provDropship  = deliveriesProvince.filter(d => d.provider_id != null);

  // Combined counts
  const totalDeliveries = deliveries.length + deliveriesProvince.length;
  const totalEntregado  = limaEntregado.length + provEntregado.length;
  const totalRechazado  = limaRechazado.length + provRechazado.length;
  const totalCancelado  = limaCancelado.length + provCancelado.length;
  const totalEnProceso  = limaPending.length + provPending.length + limaInTransit.length + provInTransit.length;
  const totalDropship   = limaDropship.length + provDropship.length;
  const totalPickups    = orders.length;

  function openModal(config) { setModal(config); }
  function closeModal()      { setModal(null);   }

  const cardBase =
    'bg-white rounded-xl shadow-sm border border-slate-200 px-3 py-2.5 hover:shadow-md transition-all cursor-pointer hover:border-[#B1336E]/30 active:scale-[0.97] text-left w-full';

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">

        {/* Total Entregas */}
        <button className={cardBase} onClick={() => openModal({ title: 'Total Entregas', limaItems: deliveries, provinciaItems: deliveriesProvince })}>
          <CardContent iconBg="bg-[#B1336E]/10" iconColor="text-[#B1336E]" icon={<Package className="w-5 h-5 text-[#B1336E]" />} count={totalDeliveries} label="Total Entregas" />
        </button>

        {/* Entregados */}
        <button className={cardBase} onClick={() => openModal({ title: 'Entregados', limaItems: limaEntregado, provinciaItems: provEntregado })}>
          <CardContent iconBg="bg-[#67A43C]/10" iconColor="text-[#67A43C]" icon={<CheckCircle className="w-5 h-5 text-[#67A43C]" />} count={totalEntregado} label="Entregados" />
        </button>

        {/* Rechazados */}
        <button className={cardBase} onClick={() => openModal({ title: 'Rechazados', limaItems: limaRechazado, provinciaItems: provRechazado })}>
          <CardContent iconBg="bg-red-100" iconColor="text-red-600" icon={<XCircle className="w-5 h-5 text-red-600" />} count={totalRechazado} label="Rechazados" />
        </button>

        {/* Cancelados */}
        <button className={cardBase} onClick={() => openModal({ title: 'Cancelados', limaItems: limaCancelado, provinciaItems: provCancelado })}>
          <CardContent iconBg="bg-orange-100" iconColor="text-orange-600" icon={<Ban className="w-5 h-5 text-orange-600" />} count={totalCancelado} label="Cancelados" />
        </button>

        {/* En Proceso */}
        <button className={cardBase} onClick={() => openModal({ title: 'En Proceso', limaItems: [...limaPending, ...limaInTransit], provinciaItems: [...provPending, ...provInTransit] })}>
          <CardContent iconBg="bg-amber-100" iconColor="text-amber-600" icon={<Clock className="w-5 h-5 text-amber-600" />} count={totalEnProceso} label="En Proceso" />
        </button>

        {/* Postergados */}
        <button
          className={cardBase}
          onClick={() => openModal({ title: 'Postergados', limaItems: postponedItems, provinciaItems: [], showPostponedReason: true })}
          disabled={postponedLoading}
        >
          <CardContent
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
            icon={postponedLoading
              ? <Clock className="w-5 h-5 text-sky-400 animate-spin" />
              : <AlarmClock className="w-5 h-5 text-sky-600" />
            }
            count={postponedLoading ? '…' : postponedItems.length}
            label="Postergados"
          />
        </button>

        {/* Dropshipping */}
        {totalDropship > 0 && (
          <button className={cardBase} onClick={() => openModal({ title: 'Dropshipping', limaItems: limaDropship, provinciaItems: provDropship })}>
            <CardContent iconBg="bg-purple-100" iconColor="text-purple-600" icon={<Box className="w-5 h-5 text-purple-600" />} count={totalDropship} label="Dropship" />
          </button>
        )}

        {/* Recojos */}
        {totalPickups > 0 && (
          <button className={cardBase} onClick={() => openModal({ title: 'Recojos', tabs: [{ label: 'Pendiente', items: orders.filter(o => o.status === 'pending') }, { label: 'Completado', items: orders.filter(o => o.status === 'completed') }] })}>
            <CardContent iconBg="bg-[#E6B43C]/10" iconColor="text-[#E6B43C]" icon={<MapPin className="w-5 h-5 text-[#E6B43C]" />} count={totalPickups} label="Recojos" />
          </button>
        )}

        {/* Express — NEW */}
        {expressDeliveries.length > 0 && (
          <button
            className={cardBase}
            onClick={() => openModal({
              title: 'Express',
              expressItems: expressDeliveries,
            })}
          >
            <CardContent
              iconBg="bg-[#67A43C]/10"
              iconColor="text-[#67A43C]"
              icon={<Zap className="w-5 h-5 text-[#67A43C]" />}
              count={expressDeliveries.length}
              label="Express"
            />
          </button>
        )}

      </div>

      {modal && (
        <DeliveryListModal
          isOpen={!!modal}
          onClose={closeModal}
          title={modal.title}
          limaItems={modal.limaItems}
          provinciaItems={modal.provinciaItems}
          tabs={modal.tabs}
          showPostponedReason={modal.showPostponedReason ?? false}
          expressItems={modal.expressItems}
        />
      )}
    </>
  );
}

function CardContent({ iconBg, iconColor, icon, count, label }) {
  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <div className="relative">
        <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center`}>
          {icon}
        </div>
        <span className={`absolute -top-2 -right-3 text-xs font-extrabold font-montserrat
          bg-white border border-slate-200 rounded-full px-1.5 min-w-[1.25rem] text-center
          leading-5 ${iconColor}`}>
          {count}
        </span>
      </div>
      <span className="text-[10px] font-semibold text-slate-600 font-montserrat text-center leading-tight mt-0.5">
        {label}
      </span>
    </div>
  );
}

export default StatisticsCards;